import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import MobileGoalRowComponent from 'webapp/components/mobile/goal-row';
import MobileGoalsOrMetricsListComponent from 'webapp/components/mobile/goals-or-metrics-list';

import FilterSortGoalOnMobile from 'webapp/models-custom/filter-sort/goal-on-mobile';
import type Goal from 'webapp/models-custom/goal';

type Args = Record<string, never>;

export default class MobileGoalsListComponent extends MobileGoalsOrMetricsListComponent<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('billing') declare billingService: Service.Billing;

	visibleCheckContainerNode: HTMLElement | null = null;
	filterSort: FilterSortGoalOnMobile;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortGoalOnMobile({
			getAllItems: () => {
				return this.items;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});

		this.filterSort = filterSort;
	}

	get items(): Goal[] {
		return this.mmGoalsService.myGoalsModels;
	}

	get hideGoalsList(): boolean {
		return this.isEmpty || !this.billingService.goalsEnabled;
	}

	@cached
	get favoriteItems(): Goal[] {
		return this.filterSort.results.filter((item) => item.favorite);
	}

	@cached
	get otherItems(): Goal[] {
		const favoritesIds = this.favoriteItems.map((item) => item.id);

		const otherGoals = this.filterSort.results.filter((goal) => {
			return !favoritesIds.includes(goal.id);
		});

		return otherGoals;
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;

		this.triggerContentChange(node);
	}

	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<div id="mobile-goals">
			{{#if this.hideGoalsList}}
				<div class="list-empty">
					<div class="header">Goals</div>

					{{#if this.billingService.goalsEnabled}}
						{{#if this.isEmpty}}
							<p>
								<CommonUiIconComponent @name="OkRs" @size="lg" />
								Looks like you haven't added any goals yet. You can add them in the
								Databox web app on your computer.
							</p>
						{{/if}}
					{{else}}
						<p>
							<CommonUiIconComponent @name="OkRs" @size="lg" />
							The goals feature is not available on your plan. You can start a free
							trial or upgrade your plan in the Databox web app on your computer.
						</p>
					{{/if}}

					<a class="button size-big theme-grey" href="/goals" target="_webapp">
						Open in browser
						<CommonUiIconComponent @name="External" @size="sm" />
					</a>
				</div>
			{{else}}

				<header>
					<strong>Goals</strong>

					<FilterLineComponent @model={{this.filterSort}} />
				</header>

				<FilterNoResultsComponent @model={{this.filterSort}} />

				<div
					class="mobile-list {{if this.filterSort.noResults 'hide'}}"
					{{didInsert this.setupView}}
					{{visibleCheckContainer}}
				>

					<div class="table-ct togglebox2 always-open">
						<div class="toggler">
							FAVORITES
							<CommonUiIconComponent @name="StarFill" @size="sm" />
						</div>
						<table class="table">
							<tbody>
								{{#each this.favoriteItems as |item|}}
									<MobileGoalRowComponent @model={{item}} />
								{{/each}}
							</tbody>
						</table>
					</div>

					<div class="table-ct togglebox2 {{if this.isOthersSectionOpen 'open'}}">
						<div class="toggler" role="button" {{on "click" this.toggleOthersSection}}>
							OTHERS<CommonUiIconComponent @name="ChevronDown" @size="xs" />
						</div>
						{{#if this.isOthersSectionOpen}}
							<table class="table">
								<tbody>
									{{#each this.otherItems as |item|}}
										<MobileGoalRowComponent @model={{item}} />
									{{/each}}
								</tbody>
							</table>
						{{/if}}
					</div>

				</div>

			{{/if}}
		</div>
	</template>
}
